import type { Language, SurveySubmission } from "../types";
import { kampungOptions, getMukimLabel } from "./locations";

export type Option = {
  value: string;
  label: Record<Language, string>;
  description?: Record<Language, string>;
};

export type Question = {
  id: keyof SurveySubmission;
  type: "single" | "select" | "textarea";
  required: boolean;
  section: "basic" | "issues";
  label: Record<Language, string>;
  helper?: Record<Language, string>;
  placeholder?: Record<Language, string>;
  options?: Option[];
};

export type Step = {
  id: "welcome" | "profile" | "location" | "issues" | "review";
  title: Record<Language, string>;
  eyebrow: Record<Language, string>;
  description: Record<Language, string>;
  questionIds: Array<keyof SurveySubmission>;
};

export const defaultSubmission: SurveySubmission = {
  gender: "",
  age_group: "",
  employment_status: "",
  nama_kampung: "",
  mukim: "",
  main_issue: "",
  development_suggestion: "",
};

export const copy = {
  appName: {
    ms: "Soal Selidik Pembangunan Daerah Putatan",
    en: "Putatan District Development Survey",
  },
  dashboard: {
    ms: "Dashboard",
    en: "Dashboard",
  },
  survey: {
    ms: "Borang Soal Selidik",
    en: "Survey Form",
  },
  subtitle: {
    ms: "Maklum balas anda membantu menyusun keutamaan pembangunan daerah.",
    en: "Your feedback helps shape district development priorities.",
  },
  start: {
    ms: "Mula",
    en: "Start",
  },
  next: {
    ms: "Seterusnya",
    en: "Next",
  },
  back: {
    ms: "Kembali",
    en: "Back",
  },
  submit: {
    ms: "Hantar Maklum Balas",
    en: "Submit Feedback",
  },
  submitting: {
    ms: "Menghantar...",
    en: "Submitting...",
  },
  review: {
    ms: "Semak jawapan anda sebelum dihantar.",
    en: "Review your answers before submitting.",
  },
  required: {
    ms: "Sila lengkapkan soalan ini.",
    en: "Please complete this question.",
  },
  thankYou: {
    ms: "Terima kasih. Maklum balas anda telah diterima.",
    en: "Thank you. Your feedback has been received.",
  },
  thankYouDetail: {
    ms: "Setiap jawapan membantu memberi gambaran lebih jelas tentang keperluan masyarakat Putatan.",
    en: "Every response helps build a clearer picture of Putatan community needs.",
  },
  another: {
    ms: "Isi Respons Baru",
    en: "Submit Another Response",
  },
  words: {
    ms: "perkataan",
    en: "words",
  },
};

export const questions: Question[] = [
  {
    id: "gender",
    type: "single",
    required: true,
    section: "basic",
    label: {
      ms: "Jantina",
      en: "Gender",
    },
    options: [
      { value: "male", label: { ms: "Lelaki", en: "Male" } },
      { value: "female", label: { ms: "Perempuan", en: "Female" } },
    ],
  },
  {
    id: "age_group",
    type: "single",
    required: true,
    section: "basic",
    label: {
      ms: "Umur",
      en: "Age",
    },
    helper: {
      ms: "Pilih kategori yang paling sesuai dengan umur anda.",
      en: "Choose the category that best fits your age.",
    },
    options: [
      {
        value: "belia_15_30",
        label: { ms: "Belia", en: "Youth" },
        description: {
          ms: "15 - 30 tahun (mengikut definisi belia Malaysia)",
          en: "15 - 30 years (Malaysia youth definition)",
        },
      },
      {
        value: "dewasa_muda_18_39",
        label: { ms: "Dewasa Muda", en: "Young adult" },
        description: {
          ms: "18 - 39 tahun",
          en: "18 - 39 years",
        },
      },
      {
        value: "dewasa_pertengahan_40_59",
        label: { ms: "Dewasa Pertengahan", en: "Middle-aged" },
        description: {
          ms: "40 - 59 tahun",
          en: "40 - 59 years",
        },
      },
      {
        value: "warga_emas_60_plus",
        label: { ms: "Warga Emas", en: "Senior citizen" },
        description: {
          ms: "60 tahun ke atas",
          en: "60 years and above",
        },
      },
    ],
  },
  {
    id: "employment_status",
    type: "single",
    required: true,
    section: "basic",
    label: {
      ms: "Status pekerjaan",
      en: "Employment status",
    },
    options: [
      { value: "student", label: { ms: "Pelajar", en: "Student" } },
      {
        value: "civil_servant",
        label: { ms: "Penjawat Awam", en: "Civil servant" },
      },
      { value: "private_sector", label: { ms: "Swasta", en: "Private sector" } },
      { value: "freelancer", label: { ms: "Freelancer", en: "Freelancer" } },
      { value: "gig_worker", label: { ms: "Pekerja Gig", en: "Gig worker" } },
      {
        value: "self_employed",
        label: { ms: "Bekerja Sendiri", en: "Self-employed" },
      },
      {
        value: "farmer_fisher",
        label: { ms: "Petani/Nelayan", en: "Farmer/Fisher" },
      },
      { value: "homemaker", label: { ms: "Suri Rumah", en: "Homemaker" } },
      { value: "unemployed", label: { ms: "Tidak bekerja", en: "Unemployed" } },
    ],
  },
  {
    id: "nama_kampung",
    type: "select",
    required: true,
    section: "basic",
    label: {
      ms: "Nama kampung",
      en: "Kampung name",
    },
    helper: {
      ms: "Pilih kampung anda. Mukim akan diisi secara automatik.",
      en: "Choose your kampung. The subdistrict will be filled automatically.",
    },
    options: kampungOptions.map(({ mukim, ...option }) => option),
  },
  {
    id: "main_issue",
    type: "textarea",
    required: true,
    section: "issues",
    label: {
      ms: "Apakah isu utama yang anda hadapi di daerah Putatan?",
      en: "What is the main issue you face in Putatan district?",
    },
    helper: {
      ms: "Contoh: jalan raya, peluang pekerjaan, keselamatan, kemudahan awam, alam sekitar.",
      en: "Examples: roads, jobs, safety, public facilities, environment.",
    },
    placeholder: {
      ms: "Tulis isu utama anda di sini...",
      en: "Write your main issue here...",
    },
  },
  {
    id: "development_suggestion",
    type: "textarea",
    required: true,
    section: "issues",
    label: {
      ms: "Apakah cadangan / pembangunan yang anda inginkan untuk memajukan Daerah Putatan?",
      en: "What suggestions or development would you like to see for Putatan District?",
    },
    helper: {
      ms: "Kongsikan cadangan yang paling penting untuk komuniti anda.",
      en: "Share the suggestions that matter most to your community.",
    },
    placeholder: {
      ms: "Tulis cadangan anda di sini...",
      en: "Write your suggestion here...",
    },
  },
];

export const steps: Step[] = [
  {
    id: "welcome",
    eyebrow: { ms: "Selamat datang", en: "Welcome" },
    title: {
      ms: "Bantu tentukan keutamaan Putatan",
      en: "Help shape Putatan's priorities",
    },
    description: {
      ms: "Soal selidik ringkas ini mengambil masa lebih kurang dua minit.",
      en: "This short survey takes about two minutes to complete.",
    },
    questionIds: [],
  },
  {
    id: "profile",
    eyebrow: { ms: "Seksyen A", en: "Section A" },
    title: { ms: "Maklumat asas", en: "Basic information" },
    description: {
      ms: "Pilih maklumat yang paling tepat tentang diri anda.",
      en: "Choose the details that best describe you.",
    },
    questionIds: ["gender", "age_group"],
  },
  {
    id: "location",
    eyebrow: { ms: "Seksyen A", en: "Section A" },
    title: { ms: "Pekerjaan dan lokasi", en: "Work and location" },
    description: {
      ms: "Maklumat ini membantu dashboard melihat corak respons mengikut komuniti. Mukim akan diisi berdasarkan kampung yang dipilih.",
      en: "This helps the dashboard read response patterns across communities. The subdistrict is filled from the selected kampung.",
    },
    questionIds: ["employment_status", "nama_kampung"],
  },
  {
    id: "issues",
    eyebrow: { ms: "Seksyen B", en: "Section B" },
    title: { ms: "Isu utama dan cadangan", en: "Key issues and suggestions" },
    description: {
      ms: "Tulis pandangan anda dengan bebas. Jawapan ringkas pun memadai.",
      en: "Write freely. Short answers are perfectly fine.",
    },
    questionIds: ["main_issue", "development_suggestion"],
  },
  {
    id: "review",
    eyebrow: { ms: "Semakan", en: "Review" },
    title: { ms: "Semak sebelum hantar", en: "Review before submitting" },
    description: {
      ms: "Pastikan jawapan anda betul sebelum dihantar.",
      en: "Make sure everything looks right before submitting.",
    },
    questionIds: [],
  },
];

const ageGroupAliases: Record<string, string> = {
  under_18: "belia_15_30",
  "15_18": "belia_15_30",
  "18_30": "belia_15_30",
  "31_45": "dewasa_muda_18_39",
  "46_60": "dewasa_pertengahan_40_59",
  over_60: "warga_emas_60_plus",
};

export function normalizeAgeGroupValue(value: string) {
  return ageGroupAliases[value] ?? value;
}

export function getQuestion(id: keyof SurveySubmission) {
  const question = questions.find((item) => item.id === id);
  if (!question) {
    throw new Error(`Unknown question: ${String(id)}`);
  }
  return question;
}

export function getOptionLabel(
  questionId: keyof SurveySubmission,
  value: string,
  language: Language,
) {
  if (questionId === "mukim") {
    return getMukimLabel(value);
  }

  const question = getQuestion(questionId);
  const normalizedValue =
    questionId === "age_group" ? normalizeAgeGroupValue(value) : value;
  return question.options?.find((option) => option.value === normalizedValue)?.label[
    language
  ] ?? value;
}
